import React, { useCallback } from 'react';
import styles from './search-section.module.scss';
import { useHeaderActions } from 'store/header/use-header-actions';
import { SearchAutocomplete } from './components/search-autocomplete/search-autocomplete';
import {
    RecentEntitiesModel,
    RecentEntitiesModelWithFreeTextOption,
} from 'core/services/recent-entities-service/recent-entities-model';
import { getReportEntityFlag } from 'core/services/report-entities-service/report-entities-service';
import { useAppDispatch } from 'store/app-store-hooks';
import { setKeyValue } from 'store/app-ui/actions';
import { SEARCH_OPTION_POI_STORE_KEY } from 'components/search-options-dialog/consts';
import { TagPOI } from 'core/entities';
import { useExplorePermissions } from 'features/explore/hooks/use-explore-permissions';
import { useOpenMultiplePropertyReport } from 'hooks/use-navigation-hooks/open-property-hooks';
import { useNearbyActivityRadiusDefault } from 'hooks/use-nearby-activity-radius/use-nearby-activity-radius';
import { PlaceWithFilters } from 'features/insights/store/poi/poi-types';
import { useAppUiActions } from 'store/app-ui/use-app-ui-actions';
import { UPGRADE_PLAN_SCREEN } from 'extensions/upgrade-your-plan-dialog';
import { useGoToExplorePage } from 'router/go-to-routes/go-to-explore';
import { useIsRestrictedPoi } from 'hooks/use-restricted-poi/use-restricted-poi';
import type { ExploreConfiguration } from 'features/explore/types/types';
import type { Complex, Place, Venue } from '@placer-ui/types';
import { isMarketEntity } from 'utils/market-poi/market-poi';
import { openMarketReport } from 'utils/navigation/navigation';
import { fetchEnclosingComplexes } from 'API/enclosing-complex/enclosing-complex-api';
import { ENCLOSING_COMPLEX_FLAGS } from 'utils/enclosing-complexes-filter/enclosing-complexes-filter';
import { setEnclosingNote } from 'features/insights/components/enclosing-notification-bar/utils/utils';
import { getFeatureFlags } from 'core/flow-control';

const GO_TO_EXPLORE_COMMON_PARAMS: Pick<ExploreConfiguration, 'zoom' | 'term'> = {
    zoom: 15,
    term: '',
};

export const SearchSection = () => {
    const { openReport } = useHeaderActions();
    const dispatch = useAppDispatch();
    const { hasUnlockNearbyActivity, hasLogSelectedSearchResultPermission } =
        useExplorePermissions();
    const redirectToReport = useOpenMultiplePropertyReport();
    const filtersRadius = useNearbyActivityRadiusDefault();
    const { openUpgradePlanPopup } = useAppUiActions();
    const goToExploreNewPage = useGoToExplorePage();
    const isRestrictedPoiCallback = useIsRestrictedPoi();
    const { a11y_alternative_report_options_ff } = getFeatureFlags();

    const openAsNearbyActivityAction = useCallback(
        (entity: RecentEntitiesModel) => {
            const entityPlace: PlaceWithFilters = {
                ...(entity as Place),
                filter: {
                    config: {
                        ft_radius: filtersRadius,
                    },
                },
            };
            if (isMarketEntity(entityPlace)) {
                openMarketReport({ marketEntity: entity as Complex });
            } else {
                redirectToReport({
                    entities: [entityPlace],
                });
            }
        },
        [filtersRadius, redirectToReport],
    );

    const onSelect = async (entity: RecentEntitiesModelWithFreeTextOption) => {
        if (typeof entity === 'string' || TagPOI.isTag(entity as Place)) {
            return;
        }
        const reportEntityFlag = getReportEntityFlag(entity as Place);
        const isRestricted = isRestrictedPoiCallback(entity as Place);

        if (isRestricted) {
            goToExploreNewPage(
                {
                    center: (entity as Place).geolocation,
                    restrictions: {
                        enable_closed_pois: true,
                    },
                    ...GO_TO_EXPLORE_COMMON_PARAMS,
                },
                {
                    activeId: (entity as Place).id,
                },
            );
        } else if (reportEntityFlag) {
            const noUnlockNearbyActivity =
                !hasUnlockNearbyActivity &&
                reportEntityFlag &&
                !ENCLOSING_COMPLEX_FLAGS.includes(reportEntityFlag);

            // open upgrade plan popup if a user does not have the explore2 + nearby activity +
            // unlock nearby activity + the entity flag is not enclosing / multistory
            if (noUnlockNearbyActivity) {
                openUpgradePlanPopup(UPGRADE_PLAN_SCREEN.default);
            } else {
                const notEnclosingFlagged = !ENCLOSING_COMPLEX_FLAGS.includes(reportEntityFlag);

                if (a11y_alternative_report_options_ff) {
                    const enclosingComplexes = await fetchEnclosingComplexes([entity.id]);
                    const selectedEnclosingComplex =
                        enclosingComplexes && enclosingComplexes[entity.id]?.[0];

                    const openAsNearbyActivity = notEnclosingFlagged || !selectedEnclosingComplex;

                    if (openAsNearbyActivity) {
                        openAsNearbyActivityAction(entity);
                    } else {
                        // open with enclosing POI
                        setEnclosingNote({
                            enclosedPropertyName: entity.name,
                            enclosedParentName: selectedEnclosingComplex.name,
                        });

                        openReport({
                            entity: selectedEnclosingComplex as Venue,
                            shouldClearEnclosingNotes: false,
                        });
                    }
                } else {
                    if (notEnclosingFlagged) {
                        openAsNearbyActivityAction(entity);
                    } else {
                        dispatch(
                            setKeyValue({
                                key: SEARCH_OPTION_POI_STORE_KEY,
                                value: entity,
                            }),
                        );
                    }
                }
            }
        } else {
            openReport({ entity });
        }
    };

    return (
        <div className={styles.searchSectionWrapper} data-testid="search-section">
            <SearchAutocomplete
                onSelection={onSelect}
                logSelectedResult={hasLogSelectedSearchResultPermission}
            />
        </div>
    );
};
